import { useCan } from '@/components/can/Can';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { Tabs } from '@/components/tabs/Tabs';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useWithdrawalsSetup } from '@/features/configuration-management/withdrawal-setup/affiliate/hooks/useWithdrawalsSetupQuery';
import { WithdrawalAffiliate } from '@/features/configuration-management/withdrawal-setup/affiliate/table';
import { useCustomerWithdrawalList } from '@/features/configuration-management/withdrawal-setup/customer/hooks/useCustomerWithdrawalQuery';
import { WithdrawalCustomer } from '@/features/configuration-management/withdrawal-setup/customer/table';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { Payment } from '@/icons';
import { SearchSchemaInput, createFileRoute, useSearch } from '@tanstack/react-router';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum([
    'minAmount',
    'maxAmount',
    'requestingDayFrom',
    'requestingDayTo',
    'withdrawableDate',
    'withdrawalType',
    'description',
    'withdrawalRange',
  ]),
  desc: z.boolean(),
});

const withdrawalsSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),

    minAmount: z
      .number()
      .refine((val) => !isNaN(val) && val >= 0, { message: 'Invalid min amount' })
      .optional(),

    maxAmount: z
      .number()
      .refine((val) => !isNaN(val) && val >= 0, { message: 'Invalid max amount' })
      .optional(),

    page: z.coerce.number().positive().catch(0),
    fetch: z.coerce.number().positive().max(100).catch(10),
    activeTab: z.coerce
      .string()
      .pipe(z.enum(['6', '3']))
      .default('6'),
    q: z
      .any()
      .optional()
      .transform((val) => (val == null ? undefined : String(val))),
  })
  .passthrough();

export const Route = createFileRoute('/(app)/configuration-management/withdrawals-setup/')({
  loader: () => {
    return {
      crumb: <CrumbTitle id="configurationManagement.withdrawalSetup.crumb.withdrawalManagement" icon={Payment} />,
      title: <Translation id="configurationManagement.withdrawalSetup.crumb.withdrawalManagement" />,
    };
  },
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => withdrawalsSearchSchema.parse(search),
});

function RouteComponent() {
  const { activeTab } = useSearch({ from: '/(app)/configuration-management/withdrawals-setup/' });
  const { withdrawals, pagination, isLoading } = useWithdrawalsSetup(activeTab, !!activeTab);
  const { customerWithdrawals, pagination: meta, loading } = useCustomerWithdrawalList(activeTab, !!activeTab);

  const { t } = useDynamicLocale();

  const tabItems = [
    {
      label: t('configurationManagement.charges.table.affiliate'),
      value: '6',
      content: <WithdrawalAffiliate data={withdrawals} isLoading={isLoading} pagination={pagination} />,
    },
    {
      label: t('configurationManagement.charges.table.customer'),
      value: '3',
      content: <WithdrawalCustomer data={customerWithdrawals} isLoading={loading} pagination={meta} />,
      disabled: !useCan({ allOf: [PERMS.withdrawal.customer.list] }),
    },
  ];

  const [activeTabProp, setActiveTabProp] = useQueryState('activeTab', parseAsString.withDefault('6'));
  const [, setPage] = useQueryState('page', parseAsInteger.withDefault(0));
  const [, setSearchQuery] = useQueryState('q', parseAsString);

  const onTabChange = (tab: string): void => {
    void Promise.all([setActiveTabProp(tab), setPage(0), setSearchQuery(null)]);
  };

  return (
    <Tabs
      className="rounded-[8px] shadow bg-muted"
      listClassName="bg-muted"
      triggerClassName="bg-muted rounded-t-[8px] px-6 py-3"
      defaultActive={activeTab ?? activeTabProp}
      value={activeTab ?? activeTabProp}
      items={tabItems}
      onChange={onTabChange}
    />
  );
}
